import { SubscriptionType } from "@/types/enums/SubscriptionType";

/**
 * The plans whose monthly quotas actually roll.
 *
 * Mirrors cron-maintenance's `MONTHLY_EMAIL_QUOTA` key set — the very map the
 * renewal loop iterates — so this list and the writer cannot disagree about
 * who gets a reset date. FREE / FREE_YEAR are absent on purpose (their Velo
 * allowance is a LIFETIME one), and so is every EVENT_* / add-on type.
 *
 * Used to decide whether "no reset date stored" is an ANSWER or just missing
 * information: for these plans a missing date means the pipeline has not
 * stamped this company yet, and the UI must stay quiet rather than claim the
 * credits never renew.
 */
export const MONTHLY_RENEWING_PLANS: ReadonlySet<string> = new Set([
  SubscriptionType.ESSENTIAL,
  SubscriptionType.ESSENTIAL_YEAR,
  SubscriptionType.ADVANCED,
  SubscriptionType.ADVANCED_YEAR,
  SubscriptionType.PROFESSIONAL,
  SubscriptionType.PROFESSIONAL_YEAR,
]);

/**
 * Format a quota reset instant for display.
 *
 * Always rendered in **UTC**. The instant is a billing-period boundary written
 * by a cron that runs at ~03:00 UTC, not a local appointment: formatting it in
 * the viewer's zone moved the calendar day for everyone from UTC-4 westwards,
 * so half the world was told their credits renew a day earlier than they do.
 * The UTC calendar day is the one the writer means, and it is the same date for
 * every viewer.
 */
export function formatQuotaResetDate(iso: string, locale: string): string {
  return new Date(iso).toLocaleDateString(locale, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    timeZone: "UTC",
  });
}
